import { formatAxisValue2 } from '../../lib/format'
import { chartFrame, chartTicks, nextCalendarDay, showsDayLabel, type Clip } from '../../lib/stateGeometry'
import { StateTip } from './StateTip'
import { useBoxSize } from './useBoxSize'

/**
 * One of the prototype's two timelines (R72): the stored value of one axis per
 * loaded date, a dashed zero line, the selected date's rule, and one transparent
 * column per date that selects it.
 *
 * Gaps stay gaps. A line joins two values only when both are stored and their
 * dates are consecutive calendar days. With two or more stored values a missing
 * one is the prototype's "×" mark on its date; with ONE stored value there is no
 * trend to draw — the one point is shown, and nothing at all is drawn or
 * selectable for a date that has no value.
 */
export interface ChartDay {
  id: string
  date: string
  /** "12", under the chart. */
  dayNum: string
  /** "12 Sep" / "12 September". */
  short: string
  long: string
  value: number | null
}

export function StateChart({ axis, name, days, selectedIndex, clip, single, onSelect }: {
  axis: 'activation' | 'balance'
  name: string
  days: readonly ChartDay[]
  selectedIndex: number
  clip: Clip
  /** Fewer than two stored values: draw the point(s) there are, and no gap marks. */
  single: boolean
  onSelect: (id: string) => void
}) {
  const [box, size] = useBoxSize<HTMLDivElement>()
  const w = size.width
  const h = size.height
  const n = days.length
  const f = w === 0 ? null : chartFrame(w, h, n, clip)
  const val = (v: number | null) => (v === null ? 'Unavailable' : `${formatAxisValue2(v)} z`)

  const segments: string[][] = []
  let run: string[] = []
  days.forEach((d, i) => {
    if (f === null) return
    const joined = i !== 0 && days[i - 1].value !== null && nextCalendarDay(days[i - 1].date, d.date)
    if (d.value === null || !joined) { if (run.length !== 1 && run.length !== 0) segments.push(run); run = [] }
    if (d.value !== null) run.push(`${f.x(i)},${f.y(d.value)}`)
  })
  if (run.length !== 1 && run.length !== 0) segments.push(run)

  return <div id={`state-chart-${axis}`} className="bp-chart" ref={box} data-testid={`state-chart-${axis}`}>
    {f !== null && <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img"
      aria-label={`${name} by recording date, in baseline-relative z units. Missing values remain gaps.`}>
      {chartTicks(clip).map((tick) => <g key={tick}>
        <line className={tick === 0 ? 'bp-gridline bp-gridline--zero' : 'bp-gridline'} x1={f.l} y1={f.y(tick)} x2={f.right} y2={f.y(tick)} />
        <text x={f.tickX} y={f.tickY(tick)} textAnchor="end">{String(tick)}</text>
      </g>)}
      <text x="0" y="8">z</text>
      <line className="bp-rule" x1={f.x(selectedIndex)} y1={f.top} x2={f.x(selectedIndex)} y2={f.bottom} />
      {segments.map((points) => <polyline key={points[0]} className="bp-line" points={points.join(' ')} />)}
      {days.map((d, i) => <g key={d.id}>
        {d.value !== null
          ? <circle className="bp-point" cx={f.x(i)} cy={f.y(d.value)} r={i === selectedIndex ? 5 : 3.5} data-testid={`state-chart-${axis}-point`} />
          : !single && <text x={f.x(i)} y={f.gapY} textAnchor="middle" data-testid={`state-chart-${axis}-gap`}>×</text>}
        {showsDayLabel(w, i) && <text x={f.x(i)} y={f.labelY} textAnchor="middle">{d.dayNum}</text>}
      </g>)}
    </svg>}
    {f !== null && days.map((d, i) => {
      if (single && d.value === null) return null
      const span = f.pick(i)
      return <StateTip key={d.id} pin={false} text={`${d.short} · ${name} ${val(d.value)}`}>
        {(tip) => <button type="button" className="bp-chartpick" {...tip} aria-pressed={i === selectedIndex}
          aria-label={`${d.long}: ${name.toLowerCase()} ${val(d.value)}`} style={{ left: span.left, width: span.width }}
          onClick={() => onSelect(d.id)} />}
      </StateTip>
    })}
  </div>
}
